import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { readingAPI } from '../utils/api';
import { BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import './BibleReader.css';

const BibleReader = ({ readingText, title, selectedLanguage }) => {
  const { user } = useAuth();
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    fetchReadings();
  }, [readingText, selectedLanguage]);

  const fetchReadings = async () => {
    if (!readingText) return;

    setLoading(true);
    setError('');

    try {
      const response = await readingAPI.fetchReading(readingText, selectedLanguage || 'english');
      const fetchedReadings = response.data.readings;

      setReadings(fetchedReadings);
      
      const initialExpanded = {};
      fetchedReadings.forEach((_, idx) => {
        initialExpanded[idx] = idx === 0;
      });
      setExpandedSections(initialExpanded);
    } catch (err) {
      console.error('Error fetching readings:', err);
      setError('Failed to load Bible text');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (index) => {
    setExpandedSections(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  if (loading) {
    return (
      <div className="bible-reader-loading">
        <BookOpen size={32} className="spin" />
        <p>Loading Bible text...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bible-reader-error">
        <p>{error}</p>
      </div>
    );
  }

  if (readings.length === 0) {
    return (
      <div className="bible-reader-empty">
        <p>No readings available</p>
      </div>
    );
  }

  const getTeluguTitle = (englishTitle) => {
    const titleMap = {
      '📖 Old Testament': '📖 పాత నిబంధన',
      'Old Testament': 'పాత నిబంధన',
      '✝️ New Testament': '✝️ కొత్త నిబంధన',
      'New Testament': 'కొత్త నిబంధన',
      '🎵 Psalms & Proverbs': '🎵 కీర్తనలు & సామెతలు',
      'Psalms & Proverbs': 'కీర్తనలు & సామెతలు'
    };
    return titleMap[englishTitle] || englishTitle;
  };

  const displayTitle = selectedLanguage === 'telugu' ? getTeluguTitle(title) : title;

  return (
    <div className="bible-reader">
      <div className="bible-reader-header">
        <h3 className={selectedLanguage === 'telugu' ? 'telugu-text' : ''}>{displayTitle}</h3>
      </div>

      {readings.map((reading, idx) => {
        const isExpanded = expandedSections[idx];

        return (
          <div key={idx} className="bible-section">
            <button 
              className="bible-section-header"
              onClick={() => toggleSection(idx)}
            >
              <div className="section-title">
                <BookOpen size={20} />
                <span className={selectedLanguage === 'telugu' ? 'telugu-text' : ''}>{reading.book} {reading.chapter}</span>
              </div>
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>

            {isExpanded && (
              <div className="bible-verses">
                {reading.verses.map((verse, vIdx) => (
                  <div key={vIdx} className={`bible-verse ${selectedLanguage === 'telugu' ? 'telugu' : ''}`}>
                    <span className="verse-number">{verse.verseNumber}</span>
                    <span className="verse-text">{verse.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BibleReader;
